// Constants
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { DefaultOrderHint } from "@ms/uno-constants/lib/local/OrderHintConstants";
import cloneDeep from "lodash/cloneDeep";
import { CustomFieldChoiceExtensionKeys } from "../../../service/planner/extensions/ICustomFieldChoiceExtensionsResource";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
/**
 * The custom field choice entity implementation.
 */ export class CustomFieldChoice extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed) {
        return {
            displayName: changed.displayName,
            orderHint: changed.orderHint,
            color: changed.color !== undefined ? this.color != null ? CustomFieldChoice.colorDataToResource(this.color) : null : undefined,
            extensions: changed.extensions && this.serializeExtensionsDiff(changed.extensions)
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            color: (value)=>value == null ? value : CustomFieldChoice.partialColorResourceToColorData(value),
            extensions: (value)=>CustomFieldChoice.deserializeExtensions(value)
        });
    }
    static fromResource(resource) {
        const extensions = this.deserializeExtensions(resource.extensions);
        return new CustomFieldChoice({
            id: resource.id,
            version: resource["@etag"],
            displayName: resource.displayName,
            orderHint: resource.orderHint ?? DefaultOrderHint,
            color: resource.color != null ? CustomFieldChoice.resourceToColorData(resource.color) : null,
            extensions
        });
    }
    static deserializeExtensions(extensions) {
        const output = {
            orderHint: DefaultOrderHint
        };
        // First parse the order hint data if it exists. Unlike colorData (decorative, safe to lose),
        // a malformed orderHintData payload throws because orderHint is structural — falling back to
        // the default would collapse every corrupted choice to the same position. Matches Bucket.
        const serializedOrderHintData = extensions[CustomFieldChoiceExtensionKeys.orderHintData];
        if (serializedOrderHintData !== undefined && serializedOrderHintData !== null) {
            try {
                const orderHintExtensionData = JSON.parse(serializedOrderHintData);
                if (typeof orderHintExtensionData.orderHint === "string") {
                    output.orderHint = orderHintExtensionData.orderHint;
                }
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=orderHintData]`);
            }
        }
        // Read color data from the colorData extension key
        // Value is a stringified JSON object: "{\"backgroundColor\": \"#d8cce7\", \"foregroundColor\": \"#401b6c\"}"
        const serializedColorData = extensions[CustomFieldChoiceExtensionKeys.colorData];
        if (serializedColorData !== undefined) {
            if (serializedColorData === null) {
                output.colorData = null;
            } else {
                try {
                    const colorData = JSON.parse(serializedColorData);
                    if (colorData.backgroundColor !== undefined && colorData.foregroundColor !== undefined) {
                        output.colorData = {
                            backgroundColor: colorData.backgroundColor,
                            foregroundColor: colorData.foregroundColor
                        };
                    }
                } catch  {
                // Ignore malformed colorData extension data
                }
            }
        }
        return output;
    }
    getClone() {
        return new CustomFieldChoice({
            id: this.id,
            version: this.version,
            displayName: this.displayName,
            orderHint: this.orderHint,
            color: cloneDeep(this.color),
            extensions: cloneDeep(this.extensions)
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            displayName: this.displayName,
            orderHint: this.orderHint,
            color: this.color != null ? CustomFieldChoice.colorDataToResource(this.color) : null,
            extensions: this.serializeExtensions()
        };
    }
    forCreate() {
        return {
            displayName: this.displayName,
            ...CustomFieldChoice.isFirstPartyFieldsEnabled() && {
                orderHint: this.orderHint,
                color: this.color != null ? CustomFieldChoice.colorDataToResource(this.color) : null
            },
            extensions: this.serializeExtensions()
        };
    }
    /**
     * Gets the color of the choice. Gated by the `EnableCustomFieldChoiceFirstPartyFields` flight: when the
     * flight is on, returns the first-class `color` as-is; otherwise returns the extensions-based value.
     */ getChoiceColor() {
        return CustomFieldChoice.isFirstPartyFieldsEnabled() ? this.color : this.extensions.colorData ?? null;
    }
    /**
     * Gets the order hint for the choice. Gated by the `EnableCustomFieldChoiceFirstPartyFields` flight: when
     * the flight is on, returns the first-class `orderHint` as-is; otherwise returns the extensions-based value.
     */ getOrderHint() {
        return CustomFieldChoice.isFirstPartyFieldsEnabled() ? this.orderHint : this.extensions.orderHint;
    }
    /**
     * Sets the choice color on the extensions and, when the `EnableCustomFieldChoiceFirstPartyFields` flight is
     * on, also on the first-class `color` property (dual-write); otherwise only the extensions are updated.
     * The color is stored with a leading `#` (7 characters); the `#` is only stripped when the entity is
     * converted to its service resource form (e.g. {@link forCreate}/{@link forSerialization}/forUpdate).
     * @param color The color whose values include a leading `#`, or `null` to clear colors
     * @returns A new ICustomFieldChoice instance with the updated colors
     */ setChoiceColor(color) {
        const withExtensions = this.setProperty("extensions", {
            ...this.extensions,
            colorData: color
        });
        return CustomFieldChoice.isFirstPartyFieldsEnabled() ? withExtensions.setProperty("color", color) : withExtensions;
    }
    /**
     * Sets the order hint on the extensions and, when the `EnableCustomFieldChoiceFirstPartyFields` flight is
     * on, also on the first-class `orderHint` property (dual-write); otherwise only the extensions are updated.
     * @param orderHint The order hint string value
     * @returns A new ICustomFieldChoice instance with the updated order hint
     */ setOrderHint(orderHint) {
        const withExtensions = this.setProperty("extensions", {
            ...this.extensions,
            orderHint
        });
        return CustomFieldChoice.isFirstPartyFieldsEnabled() ? withExtensions.setProperty("orderHint", orderHint) : withExtensions;
    }
    /** Whether the first-party (first-class) color/orderHint fields are enabled via flight. */ static isFirstPartyFieldsEnabled() {
        return AppContext?.appConfiguration?.flights?.EnableCustomFieldChoiceFirstPartyFields === true;
    }
    /**
     * Converts a first-class client color (`backgroundColor`/`foregroundColor` keys, values with a leading
     * `#`) to its service resource shape (`background`/`foreground` keys, 6-character hex without `#`).
     */ static colorDataToResource(color) {
        return {
            background: color.backgroundColor.replace("#", ""),
            foreground: color.foregroundColor.replace("#", "")
        };
    }
    /**
     * Converts a first-class color service resource (`background`/`foreground` keys, 6-character hex without
     * `#`) to its client model shape (`backgroundColor`/`foregroundColor` keys, values with a leading `#`).
     */ static resourceToColorData(resource) {
        return {
            backgroundColor: `#${resource.background}`,
            foregroundColor: `#${resource.foreground}`
        };
    }
    /**
     * Converts a possibly-partial first-class color service resource to the client color shape, adding the
     * leading `#` to each channel present. Only the channels present in the resource are included, so the
     * result can be merged (via applyDiffs) into an existing color without clobbering unchanged channels.
     */ static partialColorResourceToColorData(resource) {
        return {
            ...resource.background != null && {
                backgroundColor: `#${resource.background}`
            },
            ...resource.foreground != null && {
                foregroundColor: `#${resource.foreground}`
            }
        };
    }
    /**
     * Serializes only the changed extension fields for use in PATCH requests.
     * @param diff The diff containing only the changed extension properties
     */ serializeExtensionsDiff(diff) {
        const output = {};
        if (diff.colorData !== undefined) {
            if (this.extensions.colorData) {
                const resource = {
                    backgroundColor: this.extensions.colorData.backgroundColor,
                    foregroundColor: this.extensions.colorData.foregroundColor
                };
                output[CustomFieldChoiceExtensionKeys.colorData] = JSON.stringify(resource);
            } else {
                output[CustomFieldChoiceExtensionKeys.colorData] = null;
            }
        }
        if (diff.orderHint !== undefined) {
            const orderHintData = {
                orderHint: this.extensions.orderHint
            };
            output[CustomFieldChoiceExtensionKeys.orderHintData] = JSON.stringify(orderHintData);
        }
        return output;
    }
    serializeExtensions() {
        const output = {};
        if (this.extensions.colorData != null) {
            const colorData = {
                backgroundColor: this.extensions.colorData.backgroundColor,
                foregroundColor: this.extensions.colorData.foregroundColor
            };
            output[CustomFieldChoiceExtensionKeys.colorData] = JSON.stringify(colorData);
        }
        const orderHintData = {
            orderHint: this.extensions.orderHint
        };
        output[CustomFieldChoiceExtensionKeys.orderHintData] = JSON.stringify(orderHintData);
        return output;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.displayName = propertyBag.displayName;
        this.extensions = propertyBag.extensions ?? {
            orderHint: DefaultOrderHint
        };
        this.orderHint = propertyBag.orderHint;
        this.color = propertyBag.color;
    }
}
